import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from '../../controls/tempRHFControls/RHFSelect';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';

interface IVendorReportsDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of ap_vendor.fmt's Reports window (AP_VENDOR.RDF). Report Type only offers "A/P Vendor
// List" - the real Oracle window also has "A/P Reoccurring Voucher" (APVENDOR_REOCCUR.RDF), but
// that's dropped from scope (confirmed unused - only 19 rows total in AP_REOCCUR). There's no
// Hidden-Vendor filter control in the real window despite the report itself supporting one
// (P_HIDDEN) - matches the confirmed screenshot exactly, so this always requests all vendors.
const VendorReportsDialog = (props: IVendorReportsDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);

    const generateReport = async () => {
        const sort = getValues('sort');

        setLoading(true);
        try {
            await post('/v1.0/reports/ap-vendor-list', { sort, hidden: '' });
            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, 'AP-Vendor-List.pdf');
                props.hideDialog();
            } else {
                console.log('Unable to generate A/P Vendor List report.');
            }
        } catch (e) {
            console.log('Unable to generate A/P Vendor List report.', e);
        }
        setLoading(false);
    };

    // Decoded from the real WHEN-BUTTON-PRESSED trigger on this button: Hide_Window('W_Reports') +
    // Go_Block('Excel') - it's not "the vendor list in Excel format", it navigates to a wholly
    // separate window (W_Excel / VendorExcelExportDialog) with its own date-range/hidden/tax-type
    // filters and its own "vendors paid" export.
    const openExcelExport = () => {
        props.hideDialog();
        props.showDialog(DialogList.VENDOR_EXCEL_EXPORT);
    };

    return (
        <Dialog title="Vendor Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFSelect
                        name={'report'}
                        label="Report"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                        options={[
                            { value: 'vendorList', label: 'A/P Vendor List' },
                        ]}
                        defaultVal="vendorList"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                    <RHFSelect
                        name={'sort'}
                        label="Sort By"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                        options={[
                            { value: '2', label: 'Vendor Number' },
                            { value: '1', label: 'Vendor Name' },
                        ]}
                        defaultVal="2"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Excel Report'}
                    disabled={loading}
                    onClick={() => openExcelExport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default VendorReportsDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/VendorReportsDialog.tsx